import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Mic, Upload, Download, RefreshCw, Edit3, Save, X, Play, Globe, Users, Type, List, Plus, Settings, MoreVertical, Trash2, Copy, Languages, FileText } from 'lucide-react';
import TranscriptionService from '../services/transcriptionService';
import LocalWhisperService from '../services/localWhisperService';
import TranslationService from '../services/translationService';
import SimplePyannoteService from '../services/simplePyannoteService';
import TranscriptionDialog from './TranscriptionDialog';
import TranscriptionMethodDialog from './TranscriptionMethodDialog';
import { useSettings } from '../context/SettingsContext';
import GoogleTranslateService from '../services/googleTranslateService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
    gap: 6px;
  }
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    gap: 4px;
  }
  
  svg {
    width: 16px;
    height: 16px;
    
    @media (max-width: 768px) {
      width: 14px;
      height: 14px;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 3px;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 36px;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 11px;
    gap: 4px;
    min-height: 32px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 4px 6px;
    font-size: 10px;
    gap: 3px;
    min-height: 28px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const LanguageSelector = styled.select`
  padding: 6px 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 12px;
  margin-left: 8px;
`;

// Translation Language Controls
const TranslationLanguageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.surface};
  
  @media (max-width: 768px) {
    padding: 3px 6px;
    gap: 3px;
  }
`;

const TranslationLanguageLabel = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 8px;
  }
`;

const TranslationLanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 4px;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 26px;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.active ? props.theme.primary + 'dd' : props.theme.primary + '08'};
    transform: translateY(-1px);
  }

  svg {
    width: 11px;
    height: 11px;
  }
  
  @media (max-width: 768px) {
    padding: 3px 6px;
    font-size: 9px;
    gap: 2px;
    min-height: 24px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const ViewToggle = styled.div`
  display: flex;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  overflow: hidden;
`;

const ViewButton = styled.button`
  padding: 6px 12px;
  border: none;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  min-height: 32px;

  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.surface};
  }

  svg {
    width: 12px;
    height: 12px;
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 10px;
    gap: 3px;
    min-height: 28px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const TranscriptContainer = styled.div`
  flex: 1;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const TranscriptHeader = styled.div`
  padding: 8px 12px;
  background: ${props => props.theme.surface};
  border-bottom: 1px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 6px;
    gap: 4px;
  }
`;

const TranscriptInfo = styled.div`
  font-size: 10px;
  color: ${props => props.theme.textSecondary};
  
  @media (max-width: 768px) {
    font-size: 9px;
  }
  
  @media (max-width: 480px) {
    font-size: 8px;
  }
`;

const SpeakerManagement = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 3px;
  }
`;

const SpeakerButton = styled.button`
  padding: 6px 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  min-height: 30px;

  &:hover {
    background: ${props => props.theme.surface};
    transform: translateY(-1px);
  }

  svg {
    width: 12px;
    height: 12px;
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 10px;
    gap: 3px;
    min-height: 26px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 9px;
    gap: 2px;
    min-height: 24px;
    
    svg {
      width: 9px;
      height: 9px;
    }
  }
`;

const TranscriptContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  max-height: 500px; /* حد أقصى للارتفاع لضمان ظهور الـ scroll */
`;

// Segments View Components
const TranscriptBlock = styled.div`
  margin-bottom: ${props => props.compact ? '4px' : '8px'};
  padding: ${props => props.compact ? '4px 8px' : '8px 12px'};
  border-radius: 6px;
  border: 1px solid ${props => props.active ? props.theme.primary : 'transparent'};
  background: ${props => props.active ? props.theme.primary + '10' : props.theme.surface};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.theme.primary}50;
  }
`;

const BlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const BlockTime = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  font-weight: 500;
`;

const BlockSpeaker = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.color || props.theme.primary};
  background: ${props => (props.color || props.theme.primary) + '20'};
  padding: 2px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LanguageBadge = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: white;
  background: ${props => props.color || '#6c757d'};
  padding: 2px 6px;
  border-radius: 8px;
  margin-left: 4px;
`;

// New segment language badges (from TranslationPanel)
const SegmentLanguageBadges = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const SegmentLanguageBadge = styled.button`
  padding: 2px 6px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 10px;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 2px;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.active ? props.theme.primary + 'dd' : props.theme.primary + '08'};
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

// Language buttons for header
const LanguageButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 2px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 6px;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.active ? props.theme.primary + 'dd' : props.theme.primary + '08'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TranslateIcon = styled.button`
  width: 16px;
  height: 16px;
  border: none;
  background: ${props => props.theme.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 4px;

  &:hover {
    background: ${props => props.theme.primary}dd;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`;

const BlockActions = styled.div`
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${TranscriptBlock}:hover & {
    opacity: 1;
  }
`;

const BlockActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
    color: ${props => props.theme.text};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const BlockText = styled.div`
  font-size: ${props => props.compact ? '12px' : '14px'};
  line-height: ${props => props.compact ? '1.4' : '1.5'};
  color: ${props => props.theme.text};
`;

// Text View Components
const TextView = styled.div`
  font-size: 14px;
  line-height: 1.8;
  color: ${props => props.theme.text};
`;

const TextSegment = styled.span`
  color: ${props => props.color || props.theme.text};
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background 0.2s ease;

  &:hover {
    background: ${props => (props.color || props.theme.primary) + '20'};
  }

  &.active {
    background: ${props => (props.color || props.theme.primary) + '30'};
    font-weight: 600;
  }
`;

// Context Menu
const ContextMenu = styled.div`
  position: fixed;
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  min-width: 180px;
  pointer-events: auto;
`;

const ContextMenuItem = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.theme.surface};
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Speaker Management Modal
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.background};
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.theme.surface};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SpeakerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SpeakerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
`;

const ColorPicker = styled.input`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
`;

const SpeakerInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  text-align: center;
  color: ${props => props.theme.textSecondary};
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const EmptyTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  margin: 0 0 16px 0;
  max-width: 300px;
`;

// Enhanced language list - original language first, removed Auto Detect
const getLanguagesForTranscription = (originalLanguage = 'en') => {
  const allLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' }
];

  // Find original language and put it first
  const originalLang = allLanguages.find(lang => lang.code === originalLanguage);
  const otherLanguages = allLanguages.filter(lang => lang.code !== originalLanguage);
  
  return originalLang ? [originalLang, ...otherLanguages] : allLanguages;
};

const TranscriptEditor = ({ project, onUpdate, currentTime, onSeek }) => {
  const [editingBlock, setEditingBlock] = useState(null);
  const [editText, setEditText] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [viewMode, setViewMode] = useState('segments'); // 'segments' or 'text'
  const [selectedLanguage, setSelectedLanguage] = useState(project.transcriptionLanguage || 'en');
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  
  // Debug context menu state changes
  useEffect(() => {
    console.log('🔄 Context menu state changed:', contextMenu);
  }, [contextMenu]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [groups, setGroups] = useState(project.groups || []);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');

  // Language switching and display (from TranslationPanel)
  // Sync with PartsCutter's selected subtitle language
  const [displayLanguage, setDisplayLanguage] = useState(
    project?.selectedSubtitleLanguage || 'original'
  ); // 'original' or specific language code
  const [segmentLanguages, setSegmentLanguages] = useState({}); // Track per-segment language
  
  // Dialog states for better UX
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    selectableText: '',
    showProgress: false,
    progress: 0,
    progressText: ''
  });

  // Method selection dialog
  const [showMethodDialog, setShowMethodDialog] = useState(false);
  const [compactView, setCompactView] = useState(false); // Toggle for compact segments view
  
  const settings = useSettings();

  // Add safety check for settings
  const apiKeys = settings?.apiKeys || {};
  const isSettingsLoaded = settings && !settings.loading;

  // Safe transcript handling - ensure it's always an array
  const transcript = Array.isArray(project.transcript) ? project.transcript : [];
  const speakers = [...new Set(transcript.map(block => block.speaker).filter(Boolean))];
  
  // Support both old and new translation format (from TranslationPanel)
  const projectTranslations = project.translations || {};
  
  // Legacy support: if old translation exists, convert it
  if (project.translation && project.translationLanguage && !projectTranslations[project.translationLanguage]) {
    projectTranslations[project.translationLanguage] = project.translation;
  }
  
  // Show if transcript was loaded from saved data
  useEffect(() => {
    if (transcript.length > 0 && !isTranscribing) {
      console.log('✅ تم تحميل النسخة المكتوبة المحفوظة:', transcript.length, 'segments');
    }
  }, [transcript.length, isTranscribing]);
  
  // Sync groups with project.groups when project changes
  useEffect(() => {
    console.log('🔄 Project groups changed:', project.groups);
    setGroups(project.groups || []);
  }, [project.groups]);
  
  // Monitor showGroupModal state changes
  useEffect(() => {
    console.log('🎛️ showGroupModal state changed to:', showGroupModal);
  }, [showGroupModal]);

  // Sync displayLanguage with project's selectedSubtitleLanguage
  useEffect(() => {
    if (project?.selectedSubtitleLanguage) {
      setDisplayLanguage(project.selectedSubtitleLanguage);
      console.log('🌐 Syncing transcript display language with subtitle selection:', project.selectedSubtitleLanguage);
    }
  }, [project?.selectedSubtitleLanguage]);
  
  const [speakerSettings, setSpeakerSettings] = useState(() => {
    const defaultColors = ['#007bff', '#28a745', '#dc3545', '#ffc107', '#6f42c1', '#fd7e14'];
    const settings = {};
    speakers.forEach((speaker, index) => {
      settings[speaker] = {
        name: speaker,
        color: defaultColors[index % defaultColors.length]
      };
    });
    return settings;
  });

  const getSpeakerColor = (speaker) => {
    return speakerSettings[speaker]?.color || '#007bff';
  };

  const getSpeakerName = (speaker) => {
    return speakerSettings[speaker]?.name || speaker;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTranscribe = async () => {
    if (!project.videoPath) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'No Video Selected',
        message: 'Please select a video file first before starting transcription.',
        selectableText: '',
        showProgress: false
      });
      return;
    }

    // Check if settings are loaded
    if (!isSettingsLoaded) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Loading Settings',
        message: 'Please wait while we load your settings...',
        selectableText: '',
        showProgress: true,
        progress: 50,
        progressText: 'Loading configuration...'
      });
      return;
    }

    // Show method selection dialog
    setShowMethodDialog(true);
  };

  // Handle method selection and start transcription
  const handleMethodSelection = async (methodId, methodData, language) => {
    setShowMethodDialog(false);
    
    // Update selected language from dialog
    if (language) {
      setSelectedLanguage(language);
    }
    
    console.log('🎯 Method selection with speaker detection:', {
      methodId,
      language,
      speakerDetection: methodData.speakerDetection
    });
    
    if (methodId === 'whisper-local') {
      await handleLocalWhisperTranscription(language, methodData.speakerDetection);
    } else if (methodId === 'whisper-api') {
      await handleApiTranscription(language, methodData.speakerDetection);
    } else {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Method Not Implemented',
        message: `${methodData.name} is not yet implemented. Please choose a different method.`,
        selectableText: '',
        showProgress: false
      });
    }
  };

  // Handle local Whisper transcription via Desktop Service
  const handleLocalWhisperTranscription = async (languageCode = selectedLanguage, speakerDetectionSettings = null) => {
    setIsTranscribing(true);
    
    try {
      const apiKeys = { whisper: null, pyannote: null };
      const transcriptionService = new TranscriptionService(apiKeys);
      
      // Check Desktop Service first
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Checking Desktop Service',
        message: 'Connecting to BeeVids Transcription Service...',
        selectableText: '',
        showProgress: true,
        progress: 20,
        progressText: 'Checking Desktop Service connection...'
      });

      const serviceCheck = await transcriptionService.checkDesktopService();
      
      if (!serviceCheck.available) {
        setDialog({
          isOpen: true,
          type: 'warning',
          title: 'Desktop Service Not Available',
          message: serviceCheck.error || 'Desktop service is not running.',
          selectableText: `Setup Steps:\n1. Start "BeeVids Transcription Service.exe" from Desktop\n2. Make sure it's running on http://localhost:3001\n3. Try again`,
          showProgress: false
        });
        return;
      }

      // Check Local Whisper installation
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Checking Local Whisper',
        message: 'Checking if Whisper is installed...',
        selectableText: '',
        showProgress: true,
        progress: 40,
        progressText: 'Checking Python and Whisper installation...'
      });

      const whisperCheck = await transcriptionService.checkLocalWhisper();
      
      console.log('🎤 Whisper check result:', whisperCheck);
      
      if (!whisperCheck.ready) {
        // Check if the error is about connection or installation
        const isConnectionError = whisperCheck.isConnectionError || 
          (whisperCheck.error && 
           (whisperCheck.error.includes('Cannot connect') || 
            whisperCheck.error.includes('desktop service') ||
            whisperCheck.error.includes('Failed to fetch')));
        
        if (isConnectionError) {
          setDialog({
            isOpen: true,
            type: 'warning',
            title: 'Desktop Service Not Running',
            message: 'Cannot connect to desktop service',
            selectableText: `Setup Steps:\n1. Start "BeeVids Transcription Service.exe" from Desktop\n2. Make sure it's running on http://localhost:3001\n3. Check the service window or system tray\n4. Try again`,
            showProgress: false
          });
        } else {
          // Check if Python or Whisper is not installed
          const pythonInstalled = whisperCheck.python?.installed;
          const whisperInstalled = whisperCheck.whisper?.installed;
          
          let errorMessage = 'Local Whisper is not installed.';
          let setupSteps = `Setup Steps:\n1. Install Python 3.7+ from https://python.org\n2. Install Whisper: python -m pip install openai-whisper\n3. Restart Desktop Service\n4. Try again`;
          
          if (!pythonInstalled) {
            errorMessage = 'Python is not installed.';
            setupSteps = `Setup Steps:\n1. Install Python 3.7+ from https://python.org\n2. Make sure to check "Add Python to PATH" during installation\n3. Restart Desktop Service\n4. Try again`;
          } else if (!whisperInstalled) {
            errorMessage = 'Whisper is not installed.';
            setupSteps = `Setup Steps:\n1. Open PowerShell or Command Prompt\n2. Run: python -m pip install openai-whisper\n3. Wait for installation to complete\n4. Restart Desktop Service\n5. Try again`;
          }
          
          setDialog({
            isOpen: true,
            type: 'warning',
            title: 'Local Whisper Setup Required',
            message: errorMessage,
            selectableText: setupSteps,
            showProgress: false
          });
        }
        setIsTranscribing(false);
        return;
      }

      // Start transcription
      const speakerText = speakerDetectionSettings?.enabled ? ` + ${speakerDetectionSettings.quality} speaker detection` : '';
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Transcribing with Local Whisper',
        message: `Processing your video file via Desktop Service${speakerText}...`,
        selectableText: '',
        showProgress: true,
        progress: 60,
        progressText: `Running Whisper AI locally${speakerText}...`
      });

      console.log('🎤 Using Desktop Service for Local Whisper...', {
        language: languageCode,
        videoPath: project.videoPath,
        python: whisperCheck.python?.command,
        whisper: whisperCheck.whisper?.installed
      });
      
      // Get video file from project (Web App: from IndexedDB, Electron: from path)
      let videoFileOrPath = project.videoPath;
      
      console.log('📹 Project video info:', {
        videoPath: project.videoPath,
        hasVideoFile: !!project.videoFile,
        isElectron: !!window.require,
        projectId: project.id
      });
      
      // In Web App, if videoFile exists, use it (it's in IndexedDB or on Local Agent)
      if (!window.require && project.videoFile) {
        // If it's a Local Agent URL, we can use the path directly if we have it, 
        // or just let the transcription service handle the URL/Path.
        const isAgentUrl = project.videoFile.url?.startsWith('http://localhost:8001');
        
        if (isAgentUrl && project.videoPath) {
          console.log('🔗 Video is on Local Agent, using path:', project.videoPath);
          videoFileOrPath = project.videoPath;
        } else {
          try {
            // Get File object from IndexedDB
            const { getVideoFile } = await import('../utils/videoStorage');
            const videoFileData = await getVideoFile(project.id);
            
            console.log('📦 Video file from IndexedDB:', {
              found: !!videoFileData,
              hasUrl: !!videoFileData?.url,
              name: videoFileData?.name,
              size: videoFileData?.size
            });
            
            if (videoFileData && videoFileData.url) {
              // Create a File-like object from the blob URL
              console.log('🔄 Creating File object from blob URL...');
              try {
                const response = await fetch(videoFileData.url);
                if (!response.ok) {
                  throw new Error(`Failed to fetch blob URL: ${response.status}`);
                }
                const blob = await response.blob();
                const file = new File([blob], videoFileData.name, { type: videoFileData.type });
                videoFileOrPath = file;
                console.log('✅ File object created:', {
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  isFile: file instanceof File
                });
              } catch (fetchError) {
                console.error('❌ Error fetching blob URL:', fetchError);
                // Try to get file directly from IndexedDB again
                throw new Error(`Failed to load video file: ${fetchError.message}`);
              }
            } else if (isAgentUrl) {
              // Fallback for Agent URL if videoPath was missing but we have the URL
              console.log('🔗 Using Agent URL as fallback');
              videoFileOrPath = project.videoFile.url;
            } else {
              // Check if videoFile exists in project but not in IndexedDB
              // This might happen if the file wasn't saved properly
              console.error('❌ No video file data found in IndexedDB', {
                projectId: project.id,
                hasVideoFile: !!project.videoFile,
                videoFileName: project.videoFile?.name
              });
              
              // List all files in IndexedDB for debugging
              try {
                const { listAllVideoFiles } = await import('../utils/videoStorage');
                const allKeys = await listAllVideoFiles();
                console.log('📋 Available video files in IndexedDB:', allKeys);
                console.log('🔍 Looking for project ID:', project.id);
                console.log('🔍 Match found:', allKeys.includes(project.id));
              } catch (listError) {
                console.error('Error listing video files:', listError);
              }
              
              throw new Error('Video file not found in storage. Please re-import the video.');
            }
          } catch (error) {
            console.error('❌ Error getting video file from IndexedDB:', error);
            if (isAgentUrl) {
               console.log('🩹 IndexedDB failed but we have an Agent URL, continuing...');
               videoFileOrPath = project.videoPath || project.videoFile.url;
            } else {
              setDialog({
                isOpen: true,
                type: 'error',
                title: 'Video File Error',
                message: 'Failed to load video file from storage.',
                selectableText: `Error: ${error.message}\n\nPlease try:\n1. Re-import the video file\n2. Make sure the video file is still available`,
                showProgress: false
              });
              setIsTranscribing(false);
              return;
            }
          }
        }
      } else if (!window.require && !project.videoFile) {
        // Web App but no videoFile - this shouldn't happen
        console.error('❌ Web App: No videoFile in project');
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Video File Missing',
          message: 'Video file is not available. Please re-import the video.',
          selectableText: 'The video file was not found in storage. Please go back and re-import your video file.',
          showProgress: false
        });
        setIsTranscribing(false);
        return;
      }
      
      console.log('🎤 Sending to transcription service:', {
        type: typeof videoFileOrPath,
        isFile: videoFileOrPath instanceof File,
        isString: typeof videoFileOrPath === 'string',
        value: videoFileOrPath instanceof File ? videoFileOrPath.name : videoFileOrPath
      });
      
      // Use Desktop Service API
      const result = await transcriptionService.transcribeWithLocalWhisper(
        videoFileOrPath, // File object (Web App) or path (Electron)
        languageCode,
        'base' // model
      );

      if (result.success) {
        // Format transcript to match expected format
        const formattedTranscript = result.transcript.map((segment, index) => ({
          id: segment.id || `segment_${index + 1}`,
          startTime: segment.startTime || segment.start,
          endTime: segment.endTime || segment.end,
          text: segment.text,
          speaker: segment.speaker || 'Speaker 1',
          confidence: segment.confidence || 0.9,
          language: result.language || languageCode,
          translations: {}
        }));

        onUpdate({ 
          transcript: formattedTranscript, 
          status: 'transcribed', 
          originalLanguage: result.language || languageCode,
          localVideoPath: result.localVideoPath || project.localVideoPath
        });
        
        const speakerDetailsText = speakerDetectionSettings?.enabled 
          ? `\n- Speaker Detection: ${speakerDetectionSettings.quality} quality (will be applied)`
          : '';
          
        setDialog({
          isOpen: true,
          type: 'success',
          title: 'Local Transcription Complete',
          message: `Successfully transcribed ${formattedTranscript.length} segments via Desktop Service!`,
          selectableText: `Transcription Details:\n- Method: Local Whisper (Desktop Service)\n- Segments: ${formattedTranscript.length}\n- Language: ${result.language || languageCode}${speakerDetailsText}\n- Cost: FREE!\n- Service: http://localhost:3001`,
          showProgress: false
        });
      } else {
        throw new Error(result.error || 'Local transcription failed');
      }
      
    } catch (error) {
      console.error('❌ Local Whisper transcription error:', error);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Local Transcription Failed',
        message: 'An error occurred during local transcription.',
        selectableText: `Error Details:\n${error.message}\n\nTroubleshooting:\n1. Make sure VidoAI Local Agent is running\n2. Check http://localhost:8001/status in browser\n3. Verify faster-whisper is installed (run start.bat)\n4. Ensure video file path is accessible\n5. Try with a shorter video file first`,
        showProgress: false
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  // Handle API transcription  
  const handleApiTranscription = async (languageCode = selectedLanguage, speakerDetectionSettings = null) => {
    // Check if API keys are configured
    if (!apiKeys.whisper) {
      const selectedLangName = getLanguagesForTranscription(project.transcriptionLanguage).find(l => l.code === languageCode)?.name || languageCode;
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'API Keys Required',
        message: 'OpenAI Whisper API requires an API key. Please configure it in Settings.',
        selectableText: `Selected language: ${selectedLangName}\n\nRequired API Keys:\n- OpenAI Whisper API Key: ${apiKeys.whisper ? '✓ Configured' : '✗ Missing'}\n- Pyannote Speaker Diarization: ${apiKeys.pyannote ? '✓ Enabled (Better speaker detection)' : '✗ Disabled (Basic speaker detection)'}\n- DeepL Translation API Key: ${apiKeys.deepL ? '✓ Configured' : '✗ Missing (Optional)'}`,
        showProgress: false
      });
      return;
    }

    setIsTranscribing(true);
    
    // Show progress dialog
    const speakerText = speakerDetectionSettings?.enabled ? ` + ${speakerDetectionSettings.quality} speaker detection` : '';
    setDialog({
      isOpen: true,
      type: 'loading',
      title: 'Transcribing with OpenAI API',
      message: `Processing your video file with OpenAI Whisper API${speakerText}...`,
      selectableText: '',
      showProgress: true,
      progress: 10,
      progressText: `Initializing transcription service${speakerText}...`
    });
    
    try {
      const transcriptionService = new TranscriptionService(apiKeys);
      const selectedLangName = getLanguagesForTranscription(project.transcriptionLanguage).find(l => l.code === languageCode)?.name || languageCode;
      
      // Update progress
      setDialog(prev => ({
        ...prev,
        progress: 30,
        progressText: `Uploading video to ${selectedLangName} transcription service...`
      }));
      
      // Prepare transcription options with enhanced speaker detection
      const transcriptionOptions = {
        includeSpeakerDiarization: speakerDetectionSettings?.enabled ?? !!apiKeys.pyannote,
        pyannoteApiKey: apiKeys.pyannote,
        speakerDetection: speakerDetectionSettings || { enabled: !!apiKeys.pyannote, quality: 'standard' },
        speakerDetectionQuality: speakerDetectionSettings?.quality || 'standard'
      };
      
      console.log('🎤 API transcription with speaker detection:', transcriptionOptions);
      
      const result = await transcriptionService.transcribeVideo(
        project.videoPath, 
        languageCode,
        transcriptionOptions
      );
      
      if (result.success) {
        // Update progress
        setDialog(prev => ({
          ...prev,
          progress: 90,
          progressText: 'Processing transcript data...'
        }));
        
        // Update the project with the new transcript
        onUpdate({ 
          transcript: result.transcript, 
          status: 'transcribed', 
          originalLanguage: result.language 
        });
        
        // Show success dialog
        setDialog({
          isOpen: true,
          type: 'success',
          title: 'API Transcription Complete',
          message: `Successfully transcribed ${result.transcript.length} segments using OpenAI API.`,
          selectableText: `Transcription Details:\n- Method: OpenAI Whisper API\n- Segments: ${result.transcript.length}\n- Language: ${result.language || languageCode}\n- Duration: ${Math.round(result.duration || 0)} seconds\n- Speaker Detection: ${apiKeys.pyannote ? 'Enhanced (Pyannote)' : 'Basic (OpenAI)'}${apiKeys.pyannote ? '\n- Pyannote API: Active' : ''}`,
          showProgress: false
        });
      } else {
        throw new Error(result.error || 'Transcription failed');
      }
      
    } catch (error) {
      console.error('Transcription error:', error);
      
      // Special handling for quota exceeded error
      if (error.message.includes('quota') || error.message.includes('billing')) {
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'API Quota Exceeded',
          message: 'Your OpenAI API quota has been exceeded. Consider using Local Whisper instead.',
          selectableText: `Error Details:\n${error.message}\n\nSolutions:\n1. Use Local Whisper (completely free)\n2. Add credits to your OpenAI account\n3. Wait for your quota to reset\n4. Use a different API service`,
          showProgress: false
        });
      } else {
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'API Transcription Failed',
          message: 'An error occurred during API transcription. Consider trying Local Whisper.',
          selectableText: `Error Details:\n${error.message}\n\nTroubleshooting:\n1. Try Local Whisper (free alternative)\n2. Verify your OpenAI API key is valid and has credits\n3. Check your internet connection\n4. Ensure the video file is in a supported format (MP4, AVI, MOV, MKV, WebM)\n5. Try with a shorter video file first`,
          showProgress: false
        });
      }
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleUploadTranscript = async () => {
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      try {
        const filePath = await ipcRenderer.invoke('select-file', {
          filters: [
            { name: 'Transcript Files', extensions: ['srt', 'vtt', 'json', 'txt'] }
          ]
        });
        
        if (filePath) {
          const content = await ipcRenderer.invoke('read-file', filePath);
          const parsedTranscript = parseTranscriptFile(content, filePath);
          onUpdate({
            transcript: parsedTranscript,
            status: 'transcribed'
          });
        }
      } catch (error) {
        console.error('Error uploading transcript:', error);
      }
    } else {
      // Fallback for development
      const mockTranscript = [
        {
          id: '1',
          startTime: 0,
          endTime: 3.0,
          speaker: 'Speaker 1',
          text: 'This is an uploaded transcript example.',
          language: 'en'
        },
        {
          id: '2',
          startTime: 3.0,
          endTime: 6.0,
          speaker: 'Speaker 1',
          text: 'You can upload SRT, VTT, or JSON files.',
          language: 'en'
        }
      ];
      
      onUpdate({
        transcript: mockTranscript,
        status: 'transcribed'
      });
    }
  };

  const parseTranscriptFile = (content, filePath) => {
    try {
      return JSON.parse(content);
    } catch {
      const lines = content.split('\n').filter(line => line.trim());
      return lines.map((line, index) => ({
        id: String(index + 1),
        startTime: index * 5,
        endTime: (index + 1) * 5,
        speaker: 'Speaker 1',
        text: line.trim(),
        language: 'en'
      }));
    }
  };

  const handleExportTranscript = async () => {
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      try {
        const filePath = await ipcRenderer.invoke('save-file', {
          defaultPath: `${project.name}_transcript.json`,
          filters: [
            { name: 'JSON Files', extensions: ['json'] },
            { name: 'SRT Files', extensions: ['srt'] }
          ]
        });
        
        if (filePath) {
          const content = JSON.stringify(transcript, null, 2);
          await ipcRenderer.invoke('write-file', filePath, content);
          console.log('Transcript exported successfully');
        }
      } catch (error) {
        console.error('Error exporting transcript:', error);
      }
    } else {
      const content = JSON.stringify(transcript, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name}_transcript.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleClearTranscript = () => {
    setDialog({
      isOpen: true,
      type: 'warning',
      title: '🗑️ Clear Transcript',
      message: 'Are you sure you want to clear the current transcript? This will allow you to transcribe again with different settings.',
      selectableText: `Current transcript:\n- ${transcript.length} segments\n- ${speakers.length} speakers\n- Language: ${project.transcriptionLanguage || 'Unknown'}\n\nThis action cannot be undone, but you can always transcribe again.`,
      showProgress: false,
      confirmAction: () => {
        console.log('🗑️ Clearing transcript...');
        onUpdate({ 
          transcript: [], 
          status: 'video_only',
          transcriptionLanguage: null,
          originalLanguage: null,
          speakerInfo: null,
          translations: {}
        });
        setDialog(prev => ({ ...prev, isOpen: false }));
        
        // Show success message
        setTimeout(() => {
          setDialog({
            isOpen: true,
            type: 'success',
            title: '✅ Transcript Cleared',
            message: 'Transcript has been cleared successfully! You can now transcribe again with different settings.',
            selectableText: 'You can now:\n✅ Choose a different transcription method\n✅ Select different language\n✅ Use different speaker detection quality\n✅ Try different AI models',
            showProgress: false
          });
        }, 500);
      }
    });
  };

  // Handle standalone Pyannote Speaker Detection (بدون OpenAI)
  const handlePyannoteDetection = async () => {
    if (!apiKeys.huggingface) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: '🎤 Hugging Face Token Required',
        message: 'Please configure your Hugging Face access token in Settings to use Premium Speaker Detection.',
        selectableText: `✅ PREMIUM SPEAKER DETECTION - No Python Installation Required!\n\nQuick Steps:\n1. Go to https://huggingface.co (free account)\n2. Generate token at https://hf.co/settings/tokens\n3. Accept model conditions:\n   • https://hf.co/pyannote/segmentation-3.0\n   • https://hf.co/pyannote/speaker-diarization-3.1\n4. Add token in Settings → Hugging Face API Token\n\nFeatures:\n✅ Premium quality speaker detection\n✅ Works with existing transcripts\n✅ Can detect 2-10+ speakers accurately\n✅ No local installation needed\n✅ Free tier available (1000 requests/month)\n✅ Better than integrated detection\n\n🎯 Pro Tip: Use this for best speaker detection results!`,
        showProgress: false
      });
      return;
    }

    if (!project.videoPath && transcript.length === 0) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'No Audio/Video Available',
        message: 'Please select a video file or have an existing transcript to apply speaker detection.',
        selectableText: '',
        showProgress: false
      });
      return;
    }

    try {
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Pyannote Speaker Detection',
        message: 'Analyzing speakers using Pyannote AI...',
        selectableText: '',
        showProgress: true,
        progress: 20,
        progressText: 'Initializing Pyannote service...'
      });

      const pyannoteService = new SimplePyannoteService(apiKeys.huggingface);

      // Check if we have existing transcript to enhance
      if (transcript.length > 0 && project.videoPath) {
        setDialog(prev => ({
          ...prev,
          progress: 40,
          progressText: 'Applying speaker detection to existing transcript...'
        }));

        const result = await pyannoteService.applySpeakerDetectionToTranscript(
          project.videoPath, 
          transcript
        );

        if (result.success) {
          onUpdate({ 
            transcript: result.transcript,
            speakerInfo: result.speakerInfo
          });

                  setDialog({
          isOpen: true,
          type: 'success',
          title: '🎤 Premium Speaker Detection Complete',
          message: 'Successfully enhanced your transcript with premium speaker detection!',
          selectableText: `🎯 PREMIUM RESULTS:\n- Method: Pyannote AI (Standalone Premium)\n- Total Speakers Detected: ${result.speakerInfo.totalSpeakers}\n- Enhanced Segments: ${result.transcript.length}\n- Quality: Professional Grade\n- Status: ✅ Applied to existing transcript\n\n🎉 This is the BEST speaker detection available!\nUse this method for maximum accuracy.`,
          showProgress: false
        });
        } else {
          throw new Error(result.error);
        }
      } else if (project.videoPath) {
        // Standalone speaker detection only
        setDialog(prev => ({
          ...prev,
          progress: 50,
          progressText: 'Detecting speakers in audio...'
        }));

        const result = await pyannoteService.detectSpeakers(project.videoPath);

        if (result.success) {
          setDialog({
            isOpen: true,
            type: 'success',
            title: 'Speaker Detection Complete',
            message: 'Successfully detected speakers in your audio!',
            selectableText: `Speaker Detection Results:\n- Method: Pyannote AI (Audio Only)\n- Total Speakers: ${result.totalSpeakers}\n- Speaker Segments: ${result.segments.length}\n\nNote: This only detects speakers. Use transcription to get text content.`,
            showProgress: false
          });

          // Store speaker detection results for future use
          onUpdate({ 
            speakerDetection: result,
            status: 'speakers_detected'
          });
        } else {
          throw new Error(result.error);
        }
      }

    } catch (error) {
      console.error('Pyannote detection error:', error);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Speaker Detection Failed',
        message: 'Failed to detect speakers using Pyannote.',
        selectableText: `Error Details:\n${error.message}\n\nTroubleshooting:\n1. Verify your Pyannote API key is valid\n2. Check your internet connection\n3. Ensure the audio/video file is accessible\n4. Try with a shorter audio file first`,
        showProgress: false
      });
    }
  };

  const handleTranslateSegment = async (block) => {
    if (!apiKeys.deepL) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'DeepL API Key Required',
        message: 'Please configure your DeepL API key in Settings to use translation.',
        selectableText: 'Get your free API key at: https://www.deepl.com/pro-api',
        showProgress: false
      });
      return;
    }

    try {
      setDialog({
        isOpen: true,
        type: 'loading',
        title: 'Translating Segment',
        message: 'Translating this segment...',
        selectableText: '',
        showProgress: true,
        progress: 50,
        progressText: 'Calling DeepL API...'
      });

      const translationService = new TranslationService(apiKeys);
      const result = await translationService.translateText(block.text, 'es', project.transcriptionLanguage || 'en');

      if (result.success) {
        // Add translation to the block
        // Update the project translations instead of individual block translations
        const updatedTranslations = {
          ...projectTranslations,
          es: projectTranslations.es ? 
            projectTranslations.es.map(t => t.id === block.id ? { ...t, text: result.translatedText } : t) :
            [{ id: block.id, text: result.translatedText }]
        };
        
        onUpdate({ translations: updatedTranslations });

        setDialog({
          isOpen: true,
          type: 'success',
          title: 'Segment Translated',
          message: 'Segment has been translated successfully!',
          selectableText: `Original: ${block.text}\n\nTranslated: ${result.translatedText}`,
          showProgress: false
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Translation Failed',
        message: 'Failed to translate this segment.',
        selectableText: `Error: ${error.message}`,
        showProgress: false
      });
    }
  };

  const handleBlockClick = (block) => {
    console.log('🎯 Block click - seeking to:', block.startTime);
    onSeek(block.startTime);
    
    // Note: In PartsCutter we have onPlayPause, here we don't
    // This is just for seeking in TranscriptEditor
    // The play functionality will be handled by PartsCutter's handleSeekToSegment
  };

  const handleRightClick = (e, segment) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if there's text selection
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    
    console.log('🖱️ Right click detected:', { 
      id: segment.id, 
      text: segment.text?.substring(0, 50),
      startTime: segment.startTime,
      selectedText: selectedText,
      hasSelection: selectedText.length > 0,
      clientX: e.clientX,
      clientY: e.clientY
    });
    
    let segmentData = segment;
    
    // If there's selected text in text view, calculate timing for selected portion
    if (selectedText.length > 0 && viewMode === 'text') {
      console.log('📝 Text selection detected in text view:', selectedText);
      
      // Get the selection range to understand which elements are selected
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Find segments that intersect with the selection
        let selectedSegments = [];
        let startSegment = null;
        let endSegment = null;
        
        // Get all text segments in the DOM
        const textSegments = document.querySelectorAll('[data-segment-id]');
        
        for (let element of textSegments) {
          try {
            if (range.intersectsNode(element)) {
              const segmentId = element.getAttribute('data-segment-id');
              const segment = transcript.find(s => s.id === segmentId);
              if (segment) {
                selectedSegments.push(segment);
                if (!startSegment) startSegment = segment;
                endSegment = segment;
              }
            }
          } catch (e) {
            // Fallback to text-based matching
            console.log('Using fallback selection method');
            selectedSegments = transcript.filter(block => {
              const blockText = getSegmentText(block);
              return selectedText.includes(blockText) || blockText.includes(selectedText);
            });
            break;
          }
        }
        
        if (selectedSegments.length === 0) {
          // Fallback: find segments by text content
          selectedSegments = transcript.filter(block => {
            const blockText = getSegmentText(block);
            return selectedText.includes(blockText) || blockText.includes(selectedText);
          });
        }
        
        if (selectedSegments.length > 0) {
          const firstSegment = selectedSegments[0];
          const lastSegment = selectedSegments[selectedSegments.length - 1];
          
          // Word-based timing calculation helpers
          const cleanWord = (word) => {
            if (!word) return "";
            return word.toLowerCase()
              .replace(/[\u2018\u2019]/g, "'") // curly single quotes
              .replace(/[\u201c\u201d]/g, '"') // curly double quotes
              .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"“”]/g, "") // punctuation
              .trim();
          };

          // For single segment, calculate partial timing
          if (selectedSegments.length === 1) {
            const fullText = getSegmentText(firstSegment);
            
            // Try word-based contiguous matching
            const fullWords = fullText.split(/\s+/);
            const selectedWords = selectedText.split(/\s+/);
            const fullNorm = fullWords.map(cleanWord);
            const selectedNorm = selectedWords.map(cleanWord);
            
            let matchStartWordIdx = -1;
            for (let i = 0; i <= fullNorm.length - selectedNorm.length; i++) {
              let match = true;
              for (let j = 0; j < selectedNorm.length; j++) {
                if (fullNorm[i + j] !== selectedNorm[j]) {
                  match = false;
                  break;
                }
              }
              if (match) {
                matchStartWordIdx = i;
                break;
              }
            }
            
            if (matchStartWordIdx !== -1) {
              const startCharIdx = matchStartWordIdx > 0 
                ? fullWords.slice(0, matchStartWordIdx).join(" ").length + 1 
                : 0;
              const matchEndWordIdx = matchStartWordIdx + selectedNorm.length;
              const endCharIdx = fullWords.slice(0, matchEndWordIdx).join(" ").length;
              
              const startRatio = startCharIdx / fullText.length;
              const endRatio = endCharIdx / fullText.length;
              
              const segmentDuration = firstSegment.endTime - firstSegment.startTime;
              const selectionStartOffset = startRatio * segmentDuration;
              const selectionDuration = Math.max(0.5, (endRatio - startRatio) * segmentDuration);
              
              segmentData = {
                ...firstSegment,
                startTime: firstSegment.startTime + selectionStartOffset,
                endTime: firstSegment.startTime + selectionStartOffset + selectionDuration,
                text: selectedText,
                isPartialSelection: true,
                originalSegment: firstSegment
              };
            } else {
              // Fallback to substring matching
              const textStartIndex = fullText.toLowerCase().indexOf(selectedText.toLowerCase());
              if (textStartIndex !== -1) {
                const textRatio = selectedText.length / fullText.length;
                const segmentDuration = firstSegment.endTime - firstSegment.startTime;
                const selectionStartOffset = (textStartIndex / fullText.length) * segmentDuration;
                const selectionDuration = Math.max(0.5, textRatio * segmentDuration);
                
                segmentData = {
                  ...firstSegment,
                  startTime: firstSegment.startTime + selectionStartOffset,
                  endTime: firstSegment.startTime + selectionStartOffset + selectionDuration,
                  text: selectedText,
                  isPartialSelection: true,
                  originalSegment: firstSegment
                };
              } else {
                // If match not found, use the whole segment but with selected text
                segmentData = {
                  ...firstSegment,
                  text: selectedText,
                  isPartialSelection: true,
                  originalSegment: firstSegment
                };
              }
            }
          } else {
            // Multiple segments selected
            // Refine start time of first segment and end time of last segment based on partial selections
            let start = firstSegment.startTime;
            let end = lastSegment.endTime;
            
            const firstFullText = getSegmentText(firstSegment);
            const lastFullText = getSegmentText(lastSegment);
            
            const firstWords = firstFullText.split(/\s+/);
            const lastWords = lastFullText.split(/\s+/);
            const selectedWords = selectedText.split(/\s+/);
            
            const firstNorm = firstWords.map(cleanWord);
            const lastNorm = lastWords.map(cleanWord);
            const selectedNorm = selectedWords.map(cleanWord);
            
            // Find longest suffix of firstNorm matching prefix of selectedNorm
            let matchStartWordIdx = -1;
            for (let i = 0; i < firstNorm.length; i++) {
              const suffix = firstNorm.slice(i);
              const prefix = selectedNorm.slice(0, suffix.length);
              if (JSON.stringify(suffix) === JSON.stringify(prefix)) {
                matchStartWordIdx = i;
                break;
              }
            }
            
            if (matchStartWordIdx !== -1) {
              const startCharIdx = matchStartWordIdx > 0
                ? firstWords.slice(0, matchStartWordIdx).join(" ").length + 1
                : 0;
              const ratio = startCharIdx / firstFullText.length;
              start = firstSegment.startTime + ratio * (firstSegment.endTime - firstSegment.startTime);
            }
            
            // Find longest prefix of lastNorm matching suffix of selectedNorm
            let matchLen = 0;
            for (let length = lastNorm.length; length >= 1; length--) {
              const prefix = lastNorm.slice(0, length);
              const suffix = selectedNorm.slice(-length);
              if (JSON.stringify(prefix) === JSON.stringify(suffix)) {
                matchLen = length;
                break;
              }
            }
            
            if (matchLen > 0) {
              const endCharIdx = lastWords.slice(0, matchLen).join(" ").length;
              const ratio = endCharIdx / lastFullText.length;
              end = lastSegment.startTime + ratio * (lastSegment.endTime - lastSegment.startTime);
            }

            segmentData = {
              id: `selection_${Date.now()}`,
              startTime: start,
              endTime: end,
              text: selectedText,
              speaker: firstSegment.speaker,
              isMultiSegmentSelection: true,
              isPartialSelection: true, // Keep track of partial selection
              originalSegments: selectedSegments
            };
          }
          
          console.log('📝 Calculated selection data:', segmentData);
        }
      }
    }
    
    setSelectedSegment(segmentData);
    const menuData = {
      x: e.clientX,
      y: e.clientY,
      availableLanguages: getAvailableLanguages(),
      hasTextSelection: selectedText.length > 0 && viewMode === 'text'
    };
    
    console.log('🖱️ Setting context menu with data:', menuData);
    setContextMenu(menuData);
  };

  const handleContextMenuAction = (action, languageCode = null) => {
    console.log('🎯 Context menu action:', action, 'with data:', languageCode);
    console.log('🎯 Selected segment:', selectedSegment);
    console.log('🎯 Current groups:', groups);
    console.log('🎯 Current parts:', project.parts);
    
    if (!selectedSegment) {
      console.error('❌ No selected segment!');
      return;
    }

    switch (action) {
      case 'addToPart':
        // Individual Parts functionality removed - no longer add individual parts
        console.log('📝 Add to Parts functionality has been disabled');
        alert('Individual Parts functionality has been removed. Please use Named Groups instead.');
        break;
        
      case 'createGroup':
        console.log('📁 Creating new group...');
        console.log('📁 Current showGroupModal state:', showGroupModal);
        console.log('📁 Current contextMenu state:', contextMenu);
        
        // Close context menu first
        setContextMenu(null);
        
        // Show custom modal instead of prompt()
        setGroupName('');
        setShowGroupModal(true);
        
        console.log('📁 showGroupModal set to true');
        return; // Don't close context menu again
        
      case 'confirmCreateGroup':
        console.log('📁 Confirming group creation with name:', groupName);
        console.log('📁 Selected segment:', selectedSegment);
        console.log('📁 Current groups before creation:', groups);
        
        if (groupName.trim()) {
          let newGroup;
          
          // Check if this is a multi-segment selection or complex part
          if (selectedSegment.isMultiSegmentSelection || selectedSegment.isPartialSelection) {
            console.log('📁 Creating group with complex part:', selectedSegment);
            
            // Create a part object for the group
            const partData = {
              id: `part_${Date.now()}`,
              text: selectedSegment.text,
              startTime: selectedSegment.startTime,
              endTime: selectedSegment.endTime,
              speaker: selectedSegment.speaker,
              source: selectedSegment.isMultiSegmentSelection ? 'multi-segment-selection' : 'text-selection',
              timestamp: new Date().toISOString(),
              // Add metadata for selections
              ...(selectedSegment.isPartialSelection && {
                originalSegment: selectedSegment.originalSegment,
                isPartialSelection: true
              }),
              ...(selectedSegment.isMultiSegmentSelection && {
                originalSegments: selectedSegment.originalSegments,
                isMultiSegmentSelection: true
              })
            };
            
            newGroup = {
            id: Date.now().toString(),
              name: groupName.trim(),
              segments: [], // Empty segments for part-based group
              parts: [partData], // Add part instead
              createdAt: new Date().toISOString()
            };
            
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Group Created',
              message: `Group "${groupName.trim()}" created successfully!`,
              selectableText: `Group: ${groupName.trim()}\nPart: "${selectedSegment.text?.substring(0, 100)}${selectedSegment.text?.length > 100 ? '...' : ''}"\nTime: ${formatTime(selectedSegment.startTime)} - ${formatTime(selectedSegment.endTime)}\nType: ${selectedSegment.isMultiSegmentSelection ? `Multi-segment (${selectedSegment.originalSegments?.length} segments)` : 'Text selection'}`,
              showProgress: false
            });
          } else {
            // Regular single segment - add as segment
            newGroup = {
              id: Date.now().toString(),
              name: groupName.trim(),
              segments: [selectedSegment.id],
              parts: [], // Empty parts for segment-based group
              createdAt: new Date().toISOString()
            };
            
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Group Created',
              message: `Group "${groupName.trim()}" created successfully!`,
              selectableText: `Group: ${groupName.trim()}\nSegment: "${getSegmentText(selectedSegment).substring(0, 100)}${getSegmentText(selectedSegment).length > 100 ? '...' : ''}"\nTime: ${formatTime(selectedSegment.startTime)} - ${formatTime(selectedSegment.endTime)}`,
              showProgress: false
            });
          }
          
          console.log('📁 New group created:', newGroup);
          
          const updatedGroups = [...groups, newGroup];
          console.log('📁 Updated groups array:', updatedGroups);
          
          // Update both local state and project
          setGroups(updatedGroups);
          
          // Save groups to project
          console.log('📁 Calling onUpdate with groups:', updatedGroups);
          onUpdate({ groups: updatedGroups });
          
          console.log('✅ Group created successfully');
        } else {
          console.warn('⚠️ Group name is empty');
        }
        setShowGroupModal(false);
        setSelectedSegment(null);
        break;
        
      case 'addToGroup':
        if (languageCode) { // languageCode is actually groupId in this case
          const groupId = languageCode;
          
          // Check if this is a multi-segment selection or complex part
          if (selectedSegment.isMultiSegmentSelection || selectedSegment.isPartialSelection) {
            console.log('📁 Adding complex part to group:', selectedSegment);
            
            // Create a part object similar to addToPart action
            const partData = {
              id: `part_${Date.now()}`,
              text: selectedSegment.text,
              startTime: selectedSegment.startTime,
              endTime: selectedSegment.endTime,
              speaker: selectedSegment.speaker,
              source: selectedSegment.isMultiSegmentSelection ? 'multi-segment-selection' : 'text-selection',
              timestamp: new Date().toISOString(),
              // Add metadata for selections
              ...(selectedSegment.isPartialSelection && {
                originalSegment: selectedSegment.originalSegment,
                isPartialSelection: true
              }),
              ...(selectedSegment.isMultiSegmentSelection && {
                originalSegments: selectedSegment.originalSegments,
                isMultiSegmentSelection: true
              })
            };
            
            // Add as part to group
            const updatedGroups = groups.map(group => {
              if (group.id === groupId) {
                // Check if part is already in group
                const isPartAlreadyInGroup = group.parts?.some(p => 
                  p.startTime === partData.startTime && 
                  p.endTime === partData.endTime && 
                  p.text === partData.text
                );
                
                if (isPartAlreadyInGroup) {
                  alert('This content is already in the group!');
                  return group;
                }
                
                return {
                  ...group,
                  parts: [...(group.parts || []), partData],
                  updatedAt: new Date().toISOString()
                };
              }
              return group;
            });
            
            setGroups(updatedGroups);
            onUpdate({ groups: updatedGroups });
            
            const groupName = groups.find(g => g.id === groupId)?.name;
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Added to Group',
              message: `Part added to "${groupName}" successfully!`,
              selectableText: `Group: ${groupName}\nPart: "${selectedSegment.text?.substring(0, 100)}${selectedSegment.text?.length > 100 ? '...' : ''}"\nTime: ${formatTime(selectedSegment.startTime)} - ${formatTime(selectedSegment.endTime)}\nType: ${selectedSegment.isMultiSegmentSelection ? `Multi-segment (${selectedSegment.originalSegments?.length} segments)` : 'Text selection'}`,
              showProgress: false
            });
          } else {
            // Regular single segment - add as segment
            const updatedGroups = groups.map(group => {
              if (group.id === groupId) {
                // Check if segment is already in group
                if (!group.segments?.includes(selectedSegment.id)) {
                  return {
                    ...group,
                    segments: [...(group.segments || []), selectedSegment.id],
                    updatedAt: new Date().toISOString()
                  };
                } else {
                  alert('This segment is already in the group!');
                  return group;
                }
              }
              return group;
            });
            
            setGroups(updatedGroups);
            onUpdate({ groups: updatedGroups });
            
            const groupName = groups.find(g => g.id === groupId)?.name;
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Added to Group',
              message: `Segment added to "${groupName}" successfully!`,
              selectableText: `Group: ${groupName}\nSegment: "${getSegmentText(selectedSegment).substring(0, 100)}${getSegmentText(selectedSegment).length > 100 ? '...' : ''}"\nTime: ${formatTime(selectedSegment.startTime)} - ${formatTime(selectedSegment.endTime)}`,
              showProgress: false
            });
          }
        }
        break;
        
      case 'translate':
        handleTranslateSegment(selectedSegment);
        break;
        
      case 'changeLanguage':
        if (languageCode) {
          handleSegmentLanguageChange(selectedSegment.id, languageCode);
        }
        break;
    }
    
    setContextMenu(null);
    setSelectedSegment(null);
  };

  const handleSpeakerUpdate = (oldName, newName, color) => {
    setSpeakerSettings(prev => ({
      ...prev,
      [oldName]: { name: newName, color }
    }));

    // Update transcript with new speaker name
    const updatedTranscript = transcript.map(block => ({
      ...block,
      speaker: block.speaker === oldName ? newName : block.speaker
    }));
    
    onUpdate({ transcript: updatedTranscript });
  };

  // Handle segment language change (from TranslationPanel)
  const handleSegmentLanguageChange = (blockId, languageCode) => {
    setSegmentLanguages(prev => ({
      ...prev,
      [blockId]: languageCode
    }));
  };

  // Get segment text based on individual segment language (from TranslationPanel)
  const getSegmentText = (block) => {
    const segmentLanguage = segmentLanguages[block.id] || displayLanguage;
    
    if (segmentLanguage === 'original') {
      return transcript.find(t => t.id === block.id)?.text || block.text;
    }
    
    if (projectTranslations[segmentLanguage]) {
      return projectTranslations[segmentLanguage].find(t => t.id === block.id)?.text || block.text;
    }
    
    return block.text;
  };

  // Get available languages for display (from TranslationPanel)
  const getAvailableLanguages = () => {
    const languages = [];
    
    // Original language
    if (transcript.length > 0) {
      const originalLanguageCode = project.transcriptionLanguage || 'en';
      const originalLanguageInfo = getLanguagesForTranscription(originalLanguageCode).find(l => l.code === originalLanguageCode);
      
      languages.push({
        code: 'original',
        name: originalLanguageInfo ? originalLanguageInfo.name : 'English',
        type: 'transcript',
        icon: <Languages />
      });
    }
    
    // Get translation languages and sort them with original language first
    const translationLanguages = [];
    Object.keys(projectTranslations).forEach(languageCode => {
      if (projectTranslations[languageCode] && projectTranslations[languageCode].length > 0) {
        // Get language name from Google Translate service
        const googleService = new GoogleTranslateService();
        const supportedLanguages = googleService.getSupportedLanguages();
        const languageInfo = supportedLanguages.find(l => l.code === languageCode);
        
        translationLanguages.push({
          code: languageCode,
          name: languageInfo ? languageInfo.name : languageCode.toUpperCase(),
          type: 'translation',
          icon: <Globe />,
          isOriginal: languageCode === (project.transcriptionLanguage || 'en')
        });
      }
    });
    
    // Sort so original language appears first among translations
    translationLanguages.sort((a, b) => {
      if (a.isOriginal && !b.isOriginal) return -1;
      if (!a.isOriginal && b.isOriginal) return 1;
      return a.name.localeCompare(b.name);
    });
    
    languages.push(...translationLanguages);
    
    return languages;
  };

  const getCurrentBlock = () => {
    return transcript.find(block => 
      currentTime >= block.startTime && currentTime <= block.endTime
    );
  };

  const currentBlock = getCurrentBlock();
  const availableLanguages = getAvailableLanguages();

  const renderSegmentsView = () => (
    transcript.map((block) => (
      <TranscriptBlock
        key={block.id}
        active={currentBlock?.id === block.id}
        compact={compactView}
        onClick={() => {
          console.log('📍 Segment clicked for seeking:', block.startTime);
          handleBlockClick(block);
        }}
        onContextMenu={(e) => {
          console.log('🖱️ onContextMenu triggered for segment block:', block.id);
          handleRightClick(e, block);
        }}
      >
        <BlockHeader>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <BlockTime>
              {formatTime(block.startTime)} - {formatTime(block.endTime)}
            </BlockTime>
            <BlockSpeaker color={getSpeakerColor(block.speaker)}>
              {getSpeakerName(block.speaker)}
              <LanguageBadge color="#6c757d">
                {getLanguagesForTranscription(project.transcriptionLanguage).find(l => l.code === block.language)?.code.toUpperCase() || 'EN'}
              </LanguageBadge>
              <TranslateIcon onClick={(e) => {
                e.stopPropagation();
                handleTranslateSegment(block);
              }}>
                <Globe />
              </TranslateIcon>
            </BlockSpeaker>
          </div>
          <BlockActions>
            <BlockActionButton onClick={(e) => {
              e.stopPropagation();
              console.log('▶️ Play button clicked - seeking and playing:', block.startTime);
              handleBlockClick(block); // This calls onSeek + potentially play logic
            }}>
              <Play />
            </BlockActionButton>
            <BlockActionButton onClick={(e) => {
              e.stopPropagation();
              setEditingBlock(block.id);
              setEditText(getSegmentText(block));
            }}>
              <Edit3 />
            </BlockActionButton>
            <BlockActionButton onClick={(e) => {
              e.stopPropagation();
              handleRightClick(e, block);
            }}>
              <MoreVertical />
            </BlockActionButton>
          </BlockActions>
        </BlockHeader>
        
        {editingBlock === block.id ? (
          <div>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              style={{
                width: '100%',
                minHeight: '60px',
                border: `1px solid ${props => props.theme.border}`,
                borderRadius: '4px',
                padding: '8px',
                fontSize: '14px',
                lineHeight: '1.5',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={() => {
                  const updatedTranscript = transcript.map(b =>
                    b.id === block.id ? { ...b, text: editText } : b
                  );
                  onUpdate({ transcript: updatedTranscript });
                  setEditingBlock(null);
                  setEditText('');
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  background: '#007bff',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <Save size={12} /> Save
              </button>
              <button
                onClick={() => {
                  setEditingBlock(null);
                  setEditText('');
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #6c757d',
                  borderRadius: '4px',
                  background: 'transparent',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <BlockText compact={compactView}>{getSegmentText(block)}</BlockText>
            
            {/* Translation language badges for each segment - hide in compact view */}
            {!compactView && availableLanguages.filter(lang => lang.type === 'translation').length > 0 && (
              <SegmentLanguageBadges>
                <SegmentLanguageBadge
                  active={segmentLanguages[block.id] === 'original' || (segmentLanguages[block.id] === undefined && displayLanguage === 'original')}
                  onClick={() => {
                    handleSegmentLanguageChange(block.id, 'original');
                  }}
                >
                  <Languages />
                  Original
                </SegmentLanguageBadge>
                {availableLanguages.filter(lang => lang.type === 'translation').map((language) => (
                  <SegmentLanguageBadge
                    key={language.code}
                    active={segmentLanguages[block.id] === language.code || (segmentLanguages[block.id] === undefined && displayLanguage === language.code)}
                    onClick={() => {
                      handleSegmentLanguageChange(block.id, language.code);
                    }}
                  >
                    {language.icon}
                    {language.name}
                  </SegmentLanguageBadge>
                ))}
              </SegmentLanguageBadges>
            )}
          </div>
        )}
      </TranscriptBlock>
    ))
  );

  const renderTextView = () => (
    <TextView>
      {transcript.map((block, index) => (
        <TextSegment
          key={block.id}
          data-segment-id={block.id}
          color={getSpeakerColor(block.speaker)}
          className={currentBlock?.id === block.id ? 'active' : ''}
          onClick={() => {
            console.log('📍 Text segment clicked for seeking:', block.startTime);
            handleBlockClick(block);
          }}
          onContextMenu={(e) => {
            console.log('🖱️ onContextMenu triggered for text block:', block.id);
            handleRightClick(e, block);
          }}
        >
            {getSegmentText(block)}
          {index < transcript.length - 1 ? ' ' : ''}
        </TextSegment>
      ))}
    </TextView>
  );

  return (
    <Container>
      <Header>
        <Title>
          <FileText size={14} />
          📄 Transcript Editor
        </Title>
        <Actions>
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'segments'} 
              onClick={() => setViewMode('segments')}
            >
              <List />
              Segments
            </ViewButton>
            <ViewButton 
              active={viewMode === 'text'} 
              onClick={() => setViewMode('text')}
            >
              <Type />
              Text
            </ViewButton>
          </ViewToggle>
          
          {/* Translation Language Controls - only show if transcript exists and translations available */}
          {transcript.length > 0 && availableLanguages.filter(lang => lang.type === 'translation').length > 0 && (
            <TranslationLanguageControls>
              <TranslationLanguageLabel>🌐</TranslationLanguageLabel>
              <TranslationLanguageButton
                active={displayLanguage === 'original'}
                onClick={() => {
                  setDisplayLanguage('original');
                  setSegmentLanguages({});
                }}
              >
                <Languages />
                Original
              </TranslationLanguageButton>
              {availableLanguages.filter(lang => lang.type === 'translation').map((language) => (
                <TranslationLanguageButton
                  key={language.code}
                  active={displayLanguage === language.code}
                  onClick={() => {
                    setDisplayLanguage(language.code);
                    setSegmentLanguages({});
                  }}
                >
                  {language.icon}
                  {language.code.toUpperCase()}
                </TranslationLanguageButton>
              ))}
            </TranslationLanguageControls>
          )}
          
          <ActionButton 
            variant="primary" 
            onClick={handleTranscribe}
            disabled={isTranscribing}
          >
            <Mic />
            {isTranscribing ? 'Processing...' : 'Transcribe'}
          </ActionButton>
          
          {/* Standalone Pyannote Speaker Detection Button */}
          <ActionButton 
            onClick={handlePyannoteDetection}
            disabled={isTranscribing}
            title="Premium Speaker Detection using Pyannote AI - Best quality, works with existing transcripts"
            style={{ 
              background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
              color: 'white',
              borderColor: '#6366f1'
            }}
          >
            <Users />
            🎤 Premium Speakers
          </ActionButton>
          
          <ActionButton onClick={handleUploadTranscript}>
            <Upload />
            Upload
          </ActionButton>
          
          {transcript.length > 0 && (
            <>
            <ActionButton onClick={handleExportTranscript}>
              <Download />
              Export
            </ActionButton>
              <ActionButton 
                onClick={handleClearTranscript}
                style={{ borderColor: '#dc3545', color: '#dc3545' }}
                title="Clear current transcript to start fresh"
              >
                <Trash2 />
                Clear
              </ActionButton>
            </>
          )}
        </Actions>
      </Header>

      <TranscriptContainer>
        <TranscriptHeader>
          <TranscriptInfo>
            {transcript.length > 0 
              ? `${transcript.length} segments • ${speakers.length} speakers${apiKeys.pyannote ? ' • 🎤 Enhanced' : ' • 🎤 Basic'} • ✅`
              : 'No transcript available'
            }
          </TranscriptInfo>
          
          {speakers.length > 0 && (
            <SpeakerManagement>
              <SpeakerButton onClick={() => setCompactView(!compactView)}>
                <Type />
                {compactView ? 'Detailed View' : 'Compact View'}
              </SpeakerButton>
              <SpeakerButton onClick={() => setShowSpeakerModal(true)}>
                <Users />
                Manage Speakers
              </SpeakerButton>
            </SpeakerManagement>
          )}
        </TranscriptHeader>

        <TranscriptContent>
          {transcript.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Mic size={24} color="#6c757d" />
              </EmptyIcon>
              <EmptyTitle>No Transcript Available</EmptyTitle>
              <EmptyDescription>
                Generate a transcript automatically using AI transcription or upload an existing transcript file.
              </EmptyDescription>
              <ActionButton variant="primary" onClick={handleTranscribe}>
                <Mic />
                Start Transcription
              </ActionButton>
            </EmptyState>
          ) : (
            viewMode === 'segments' ? renderSegmentsView() : renderTextView()
          )}
        </TranscriptContent>
      </TranscriptContainer>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu x={contextMenu.x} y={contextMenu.y}>
          {console.log('🎯 Context menu is rendering at position:', contextMenu.x, contextMenu.y)}
          {/* Add to Parts option removed */}
          
          {/* Show text selection info */}
          {contextMenu.hasTextSelection && selectedSegment && (
            <div style={{ 
              borderTop: '1px solid #ddd', 
              margin: '4px 0', 
              fontSize: '9px', 
              padding: '4px 12px', 
              color: '#666',
              fontStyle: 'italic',
              maxWidth: '200px',
              lineHeight: '1.2'
            }}>
              📝 Selected: "{selectedSegment.text?.substring(0, 30)}${selectedSegment.text?.length > 30 ? '...' : ''}"
              {selectedSegment.isMultiSegmentSelection && (
                <>
                  <br />
                  <span style={{ fontWeight: '600' }}>
                    🔗 {selectedSegment.originalSegments?.length} segments
                  </span>
                </>
              )}
            </div>
          )}
          
          {/* Groups Section */}
          {groups.length > 0 && (
            <>
              <div style={{ 
                borderTop: '1px solid #ddd', 
                margin: '4px 0', 
                fontSize: '10px', 
                padding: '4px 12px', 
                color: '#666',
                fontWeight: '600'
              }}>
                📁 Add to Group:
              </div>
              {groups.map((group) => (
                <ContextMenuItem 
                  key={group.id}
                  onClick={() => handleContextMenuAction('addToGroup', group.id)}
                >
                  <Plus />
                  {group.name} ({(group.segments?.length || 0) + (group.parts?.length || 0)} items)
                </ContextMenuItem>
              ))}
            </>
          )}
          
          <ContextMenuItem onClick={() => handleContextMenuAction('createGroup')}>
            <Plus />
            Create New Group
          </ContextMenuItem>
          
          <ContextMenuItem onClick={() => handleContextMenuAction('translate')}>
            <Globe />
            Translate Segment
          </ContextMenuItem>
          
          {/* Translation language options if available */}
          {contextMenu.availableLanguages && contextMenu.availableLanguages.filter(lang => lang.type === 'translation').length > 0 && (
            <>
              <div style={{ 
                borderTop: '1px solid #ddd', 
                margin: '4px 0', 
                fontSize: '10px', 
                padding: '4px 12px', 
                color: '#666' 
              }}>
                Switch Language:
              </div>
              <ContextMenuItem 
                onClick={() => handleContextMenuAction('changeLanguage', 'original')}
              >
                <Languages />
                Original
              </ContextMenuItem>
              {contextMenu.availableLanguages.filter(lang => lang.type === 'translation').map((language) => (
                <ContextMenuItem 
                  key={language.code}
                  onClick={() => handleContextMenuAction('changeLanguage', language.code)}
                >
                  {language.icon}
                  {language.name}
                </ContextMenuItem>
              ))}
            </>
          )}
        </ContextMenu>
      )}

      {/* Speaker Management Modal */}
      {showSpeakerModal && (
        <Modal onClick={() => setShowSpeakerModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Manage Speakers</ModalTitle>
              <CloseButton onClick={() => setShowSpeakerModal(false)}>
                <X />
              </CloseButton>
            </ModalHeader>
            
            <SpeakerList>
              {speakers.map(speaker => (
                <SpeakerItem key={speaker}>
                  <ColorPicker
                    type="color"
                    value={getSpeakerColor(speaker)}
                    onChange={(e) => handleSpeakerUpdate(speaker, getSpeakerName(speaker), e.target.value)}
                  />
                  <SpeakerInput
                    value={getSpeakerName(speaker)}
                    onChange={(e) => handleSpeakerUpdate(speaker, e.target.value, getSpeakerColor(speaker))}
                  />
                </SpeakerItem>
              ))}
            </SpeakerList>
          </ModalContent>
        </Modal>
      )}

      {/* Group Creation Modal */}
      {showGroupModal && (
        <Modal onClick={() => {
          console.log('📁 Modal background clicked - closing modal');
          setShowGroupModal(false);
          setSelectedSegment(null);
        }}>
          <ModalContent onClick={(e) => {
            e.stopPropagation();
            console.log('📁 Modal content clicked - preventing close');
          }}>
            <ModalHeader>
              <ModalTitle>📁 Create New Group</ModalTitle>
              <CloseButton onClick={() => {
                console.log('📁 Close button clicked');
                setShowGroupModal(false);
                setSelectedSegment(null);
              }}>
                <X />
              </CloseButton>
            </ModalHeader>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: '500',
                color: 'inherit'
              }}>
                Group Name:
              </label>
              <SpeakerInput
                value={groupName}
                onChange={(e) => {
                  console.log('📁 Group name changed to:', e.target.value);
                  setGroupName(e.target.value);
                }}
                placeholder="Enter group name..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    console.log('📁 Enter pressed - creating group');
                    handleContextMenuAction('confirmCreateGroup');
                  }
                }}
                autoFocus
              />
            </div>
            
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <ActionButton 
                onClick={() => {
                  console.log('📁 Cancel button clicked');
                  setShowGroupModal(false);
                  setSelectedSegment(null);
                }}
              >
                Cancel
              </ActionButton>
              <ActionButton 
                variant="primary"
                onClick={() => {
                  console.log('📁 Create button clicked');
                  handleContextMenuAction('confirmCreateGroup');
                }}
                disabled={!groupName.trim()}
              >
                <Plus size={12} />
                Create Group
              </ActionButton>
            </div>
          </ModalContent>
        </Modal>
      )}

      {/* Click outside to close context menu */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998
          }}
          onClick={() => {
            console.log('🖱️ Clicking outside context menu - closing');
            setContextMenu(null);
          }}
        />
      )}

      {/* Method Selection Dialog */}
      <TranscriptionMethodDialog
        isOpen={showMethodDialog}
        onClose={() => setShowMethodDialog(false)}
        onSelectMethod={handleMethodSelection}
        availableMethods={apiKeys}
        selectedLanguage={selectedLanguage}
        originalLanguage={project.transcriptionLanguage || 'en'}
      />

      {/* Transcription Dialog */}
      <TranscriptionDialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        selectableText={dialog.selectableText}
        showProgress={dialog.showProgress}
        progress={dialog.progress}
        progressText={dialog.progressText}
        confirmAction={dialog.confirmAction}
        onOpenSettings={() => {
          // Navigate to settings - you may need to adjust this based on your routing
          setDialog(prev => ({ ...prev, isOpen: false }));
          // Add navigation logic here
        }}
        onRetry={() => {
          setDialog(prev => ({ ...prev, isOpen: false }));
          handleTranscribe();
        }}
      />
    </Container>
  );
};

export default TranscriptEditor;